/**
 * The leave guard for a form with unsaved changes: a reload asks through the
 * browser, an in-app navigation to another page asks through the console's
 * own confirm. A change of query string (opening a record, closing a panel
 * form) is not leaving and never asks.
 *
 * Returns `release`: call it right before navigating away after a save, so
 * the navigation the save itself makes is not blocked. The guard reads a ref,
 * so the release takes effect in the same tick, before React re-renders.
 *
 * The in-app half needs the router's blocker, which only a data router
 * provides. Under a plain router (a test's MemoryRouter) the hook guards the
 * reload only, so a form never throws for lacking a blocker.
 */
import { useCallback, useContext, useEffect, useRef } from 'react';
import { UNSAFE_DataRouterContext, useBeforeUnload, useBlocker } from 'react-router-dom';

import { useConfirm } from '../ui/Confirm.js';

const LEAVE = {
  title: 'Leave without saving?',
  description: 'Your changes on this form have not been saved.',
  confirmLabel: 'Leave',
  tone: 'danger' as const,
};

export function useUnsavedChanges(dirty: boolean): () => void {
  const guarded = useRef(dirty);
  guarded.current = dirty;
  useBeforeUnload(
    useCallback((event: BeforeUnloadEvent) => {
      if (!guarded.current) return;
      event.preventDefault();
      event.returnValue = '';
    }, []),
  );
  const hasDataRouter = useContext(UNSAFE_DataRouterContext) !== null;
  if (hasDataRouter) {
    // `hasDataRouter` is fixed for the life of the app, so this branch is
    // taken on every render or on none: the hook order never changes.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useBlockingConfirm(guarded);
  }
  return useCallback(() => {
    guarded.current = false;
  }, []);
}

function useBlockingConfirm(guarded: { current: boolean }): void {
  const confirm = useConfirm();
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      guarded.current && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    void confirm(LEAVE).then((ok) => {
      if (ok) blocker.proceed();
      else blocker.reset();
    });
  }, [blocker, confirm]);
}
